import * as React from 'react';
import { Upload, X, ImageIcon, AlertCircle, Crop } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/utils';
import { validateImageFile, ALLOWED_IMAGE_TYPES } from '@/lib/utils/image';
import { ImageCropper, type AspectRatio } from './ImageCropper';

// ============ Types ============

interface BannerImageUploadProps {
  value: {
    file: File | null;
    preview: string | null;
  };
  onChange: (file: File | null, preview: string | null) => void;
  aspectRatio?: AspectRatio;
  minWidth?: number;
  minHeight?: number;
  className?: string;
  disabled?: boolean;
}

// ============ Component ============

export function BannerImageUpload({
  value,
  onChange,
  aspectRatio = '1:1',
  minWidth = 1080,
  minHeight = 1080,
  className,
  disabled = false,
}: BannerImageUploadProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [cropperOpen, setCropperOpen] = React.useState(false);
  const [tempImageSrc, setTempImageSrc] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Note: No cleanup needed for preview since we use data URLs (base64)
  // which don't require revocation like blob URLs

  // Handle file selection
  const handleFileSelect = React.useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }

      // Create temp URL for cropper
      const src = URL.createObjectURL(file);
      setTempImageSrc(src);
      setCropperOpen(true);
    },
    []
  );

  // Handle crop complete
  const handleCropComplete = React.useCallback(
    (file: File, preview: string) => {
      // Clean up temp blob URL
      if (tempImageSrc) {
        URL.revokeObjectURL(tempImageSrc);
        setTempImageSrc(null);
      }

      // Note: No need to revoke value.preview - it's a data URL, not a blob URL

      onChange(file, preview);
      toast.success('Image recadrée avec succès');
    },
    [onChange, tempImageSrc]
  );

  // Handle cropper close without saving
  const handleCropperClose = React.useCallback(
    (open: boolean) => {
      if (!open && tempImageSrc) {
        URL.revokeObjectURL(tempImageSrc);
        setTempImageSrc(null);
      }
      setCropperOpen(open);
    },
    [tempImageSrc]
  );

  // Handle remove image
  const handleRemove = React.useCallback(() => {
    // Note: No need to revoke preview - it's a data URL, not a blob URL
    onChange(null, null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [onChange]);

  // Handle re-crop
  const handleRecrop = React.useCallback(() => {
    if (value.file) {
      const src = URL.createObjectURL(value.file);
      setTempImageSrc(src);
      setCropperOpen(true);
    }
  }, [value.file]);

  // Drag and drop handlers
  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;
      handleFileSelect(e.dataTransfer.files);
    },
    [disabled, handleFileSelect]
  );

  // Handle click to upload
  const handleClick = React.useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  // Get aspect ratio label
  const aspectLabel = React.useMemo(() => {
    switch (aspectRatio) {
      case '1:1':
        return 'carré (1:1)';
      case '16:9':
        return 'large (16:9)';
      case '4:3':
        return 'standard (4:3)';
      default:
        return aspectRatio;
    }
  }, [aspectRatio]);

  return (
    <>
      <div className={cn('space-y-3', className)}>
        {/* Hidden file input */}
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_IMAGE_TYPES.join(',')}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={disabled}
        />

        {/* Upload zone or preview */}
        {value.preview ? (
          // Image preview
          <div className="relative rounded-xl overflow-hidden border bg-muted">
            <img
              src={value.preview}
              alt="Aperçu de la bannière"
              className="w-full h-auto max-h-64 object-contain"
            />

            {/* Overlay controls */}
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors group">
              <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  onClick={handleRecrop}
                  className="size-8"
                  disabled={disabled}
                >
                  <Crop className="size-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  onClick={handleRemove}
                  className="size-8"
                  disabled={disabled}
                >
                  <X className="size-4" />
                </Button>
              </div>
            </div>

            {/* Format badge */}
            <div className="absolute bottom-2 left-2">
              <span className="text-xs bg-black/60 text-white px-2 py-1 rounded">
                Format {aspectLabel}
              </span>
            </div>
          </div>
        ) : (
          // Upload zone
          <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClick();
              }
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'relative flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed transition-all cursor-pointer',
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <div
              className={cn(
                'size-12 rounded-full flex items-center justify-center',
                isDragging ? 'bg-primary/10' : 'bg-muted'
              )}
            >
              {isDragging ? (
                <Upload className="size-6 text-primary" />
              ) : (
                <ImageIcon className="size-6 text-muted-foreground" />
              )}
            </div>

            <div className="text-center">
              <p className="font-medium">
                {isDragging
                  ? 'Déposez votre image'
                  : 'Cliquez ou glissez une image'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Format {aspectLabel}, min {minWidth}x{minHeight}px
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                JPEG, PNG ou WebP (max 10 Mo)
              </p>
            </div>
          </div>
        )}

        {/* Requirements notice */}
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <AlertCircle className="size-4 shrink-0 mt-0.5" />
          <p>
            L'image sera automatiquement recadrée au format {aspectLabel} pour
            un affichage optimal sur les appareils mobiles.
          </p>
        </div>
      </div>

      {/* Cropper modal */}
      {tempImageSrc && (
        <ImageCropper
          open={cropperOpen}
          onOpenChange={handleCropperClose}
          imageSrc={tempImageSrc}
          aspectRatio={aspectRatio}
          onCropComplete={handleCropComplete}
          minWidth={minWidth}
          minHeight={minHeight}
        />
      )}
    </>
  );
}
